import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

/* â”€â”€â”€ Shared sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const MapifyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/mapify"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Mapify
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: '#1A73E8' }}>
          <MapPin size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Mapify</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const MapifyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Mapify · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/mapify/terms" className="font-mono text-[0.55rem] tracking-widest text-[#1A73E8] uppercase">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/mapify/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest text-[#1A73E8] uppercase shrink-0 mt-1.5">{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const MapifyTermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Mapify Terms & Conditions"
        description="Terms and conditions for the Mapify Shopify app by Smart Solutions."
        path="/apps/mapify/terms"
      />
      <MapifyTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Terms &amp; Conditions
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: March 2025
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              Please read these Terms and Conditions carefully before using Mapify. By installing or accessing the App,
              you agree to be bound by these Terms. These Terms form a legal agreement between you (the Merchant) and
              Smart Solutions, the operator of Mapify.
            </P>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Acceptance of Terms">
            <P>
              By installing or using Mapify ("the App"), you ("Merchant") agree to these Terms. If you do not agree,
              do not install or use the App. These Terms apply to all users of the App, including merchants and their customers.
            </P>
          </Section>

          <Section num="02" title="Description of Service">
            <P>
              Mapify is a Shopify checkout UI extension that embeds an interactive Google Map into the checkout flow.
              Customers can drop a pin on the map to indicate their delivery location.
            </P>
            <P>
              The App reverse-geocodes the pinned coordinates via the Google Maps API and optionally auto-fills the
              shipping address fields. The pinned location data (latitude, longitude, formatted address, and Google Maps URL)
              is stored as Shopify order metafields on your store.
            </P>
            <P>
              The App is hosted on Microsoft Azure (UAE North region) and delivered as a Software-as-a-Service (SaaS).
            </P>
          </Section>

          <Section num="03" title="Merchant Responsibilities">
            <P>
              You are responsible for correctly configuring the App in the Shopify Checkout Editor, including setting the
              correct proxy URL. You are responsible for keeping your proxy URL up to date if your domain or configuration changes.
            </P>
            <P>
              You must not use the App for any unlawful purpose or in any way that violates Shopify's API Terms of Service.
              You are responsible for communicating to your customers how location data is collected and used, including
              through your own store's privacy policy.
            </P>
          </Section>

          <Section num="04" title="Customer Data & Geolocation">
            <P>
              The App requests geolocation access from the customer's browser solely with the customer's explicit permission
              via the standard browser geolocation prompt. If permission is denied, the map loads without geolocation centering
              and no location data is collected.
            </P>
            <P>
              Pinned coordinates are processed by the App's backend to produce a formatted address and are then stored as
              order metafields on your Shopify store. This data is associated with the order and is accessible to you as the
              merchant through the Shopify admin.
            </P>
            <P>
              We do not retain customer location data independently beyond what is described in our Privacy Policy.
            </P>
          </Section>

          <Section num="05" title="Billing & Payment">
            <P>
              The App uses Shopify's Usage Billing API. You will be charged on a per-geocoding-call basis at the rate
              disclosed in the App listing or your active billing plan. Charges are billed through your Shopify account
              and are subject to Shopify's billing terms.
            </P>
            <P>
              API calls that are consumed are non-refundable, including calls made during testing or misconfiguration.
              We reserve the right to update pricing with 30 days' notice.
            </P>
          </Section>

          <Section num="06" title="Google Maps Usage">
            <P>
              The App uses the Google Maps Platform (Maps JavaScript API and Geocoding API). Your use of mapping
              functionality through Mapify is subject to the Google Maps Platform Terms of Service
              (maps.google.com/about/terms).
            </P>
            <P>
              We are not responsible for any interruptions, inaccuracies, or changes in Google Maps services that
              affect the App's functionality.
            </P>
          </Section>

          <Section num="07" title="Availability & Uptime">
            <P>
              We aim to maintain high availability of the App but do not guarantee any specific uptime or
              service-level agreement (SLA). The App may be temporarily unavailable due to maintenance, updates,
              or factors outside our control.
            </P>
            <P>
              In all cases, the App is designed to be non-blocking: if the App or map fails to load, the Shopify
              checkout will continue to function normally without interruption.
            </P>
          </Section>

          <Section num="08" title="Limitation of Liability">
            <P>
              To the fullest extent permitted by law, Smart Solutions and its affiliates shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use
              of the App, including but not limited to loss of revenue, loss of data, or delivery disputes arising from
              inaccurate location data.
            </P>
            <P>
              Our total liability shall not exceed the amount you paid for the App in the three months preceding the claim.
            </P>
          </Section>

          <Section num="09" title="Modifications to Terms">
            <P>
              We may update these Terms from time to time. If we make material changes, we will notify you via the
              Shopify admin or by email. Continued use of the App after changes become effective constitutes acceptance
              of the revised Terms.
            </P>
          </Section>

          <Section num="10" title="Contact">
            <P>
              If you have questions about these Terms, please contact us at:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" className="text-[#1A73E8] hover:underline">hello@smartsolution.ai</a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              These terms were last updated in March 2025
            </p>
            <Link
              to="/apps/mapify/privacy"
              className="font-mono text-[0.6rem] tracking-widest text-[#1A73E8] hover:underline uppercase"
            >
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </div>

      <MapifyFooter />
    </div>
  );
};
